import { shuffle } from "../Shared/Helpers.tsx";
import { MissingNumberOptions, ProblemData, ProblemFormatOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PICTURE_CELLS, PICTURE_COLUMNS, PaletteKey } from "./ColorByNumberPicture.ts";

/** Problems drawn per page before sharing answers out; enough to give most colours several facts. */
const POOL_SIZE = 600;
/** A colour's answer list is capped so the key stays printable (ADR 0058 §3). */
export const MAX_ANSWERS_PER_COLOR = 12;

type Generate = (settings: ISettingsProps) => ProblemData[][] | null;

export function factId(problem: ProblemData): string {
    return `${problem.numbers[0]}${problem.operator}${problem.numbers[1]}`;
}

/**
 * The sheet's own settings, turned into a plain fact sheet big enough to pick
 * from. Remainders and missing numbers are forced off: neither leaves a single
 * number a colour can be keyed to (§3).
 */
export function poolSettings(settings: ISettingsProps): ISettingsProps {
    return {
        ...settings,
        Format: ProblemFormatOptions.MathFacts,
        NumberOfPages: 1,
        NumberOfProblemsPerPage: POOL_SIZE,
        HalfPage: false,
        Remainders: false,
        MissingNumber: MissingNumberOptions.Off,
        // A level is a math-facts setting whose control this format does not
        // show; without this it would silently deal the picture's problems
        // (ADR 0071 §3).
        FactLevel: 0,
    };
}

/** Distinct facts for each answer. */
export function byAnswer(pool: ProblemData[]): Map<number, ProblemData[]> {
    const groups = new Map<number, ProblemData[]>();
    const seen = new Set<string>();
    for (const problem of pool) {
        // Plain division draws 0 ÷ 0 when both ranges hold a zero. A fact
        // sheet prints it; a colour cannot honestly be keyed to it.
        if (problem.operator === "÷" && problem.numbers[1] === 0) {
            continue;
        }
        const id = factId(problem);
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        const answer = problem.numbers[2];
        groups.set(answer, [...(groups.get(answer) ?? []), problem]);
    }
    return groups;
}

/**
 * Shares the answers out between the colours (§3): every colour gets one,
 * the most-needed colour first; the rest go, one at a time, to whichever
 * colour is shortest of facts for its cells, up to the per-colour cap.
 */
export function assignAnswers(
    groups: Map<number, ProblemData[]>,
    cellsPerColor: Map<PaletteKey, number>
): Map<PaletteKey, number[]> | null {
    const colors = [...cellsPerColor.keys()].sort((a, b) => cellsPerColor.get(b)! - cellsPerColor.get(a)!);
    if (groups.size < colors.length) {
        return null;
    }
    // Most facts first, with the order among equals shuffled so pages differ.
    const answers = shuffle([...groups.keys()]).sort((a, b) => groups.get(b)!.length - groups.get(a)!.length);
    const assigned = new Map<PaletteKey, number[]>(colors.map((color) => [color, []]));
    const supply = new Map<PaletteKey, number>(colors.map((color) => [color, 0]));

    const give = (color: PaletteKey, answer: number) => {
        assigned.get(color)!.push(answer);
        supply.set(color, supply.get(color)! + groups.get(answer)!.length);
    };

    colors.forEach((color, i) => give(color, answers[i]));
    for (const answer of answers.slice(colors.length)) {
        const open = colors.filter((color) => assigned.get(color)!.length < MAX_ANSWERS_PER_COLOR);
        if (open.length === 0) {
            break;
        }
        // Shortest relative to its cells gets the next answer.
        const neediest = open.reduce((best, color) =>
            supply.get(color)! / cellsPerColor.get(color)! < supply.get(best)! / cellsPerColor.get(best)! ? color : best
        );
        if (supply.get(neediest)! >= cellsPerColor.get(neediest)!) {
            break;
        }
        give(neediest, answer);
    }
    for (const list of assigned.values()) {
        list.sort((a, b) => a - b);
    }
    return assigned;
}

/** One picture's worth of problems, in cell order, each tagged with its cell and colour. */
export function buildColorByNumberPage(pool: ProblemData[]): ProblemData[] | null {
    const groups = byAnswer(pool);
    const cellsPerColor = new Map<PaletteKey, number>();
    for (const color of PICTURE_CELLS) {
        cellsPerColor.set(color, (cellsPerColor.get(color) ?? 0) + 1);
    }
    const assigned = assignAnswers(groups, cellsPerColor);
    if (!assigned) {
        return null;
    }

    // Each colour's facts, shuffled, dealt round-robin over its cells.
    const decks = new Map<PaletteKey, ProblemData[]>();
    const dealt = new Map<PaletteKey, number>();
    for (const [color, answers] of assigned) {
        decks.set(color, shuffle(answers.flatMap((answer) => groups.get(answer)!)));
        dealt.set(color, 0);
    }

    const page: ProblemData[] = [];
    PICTURE_CELLS.forEach((color, index) => {
        const deck = decks.get(color)!;
        const left = index % PICTURE_COLUMNS !== 0 ? page[index - 1] : undefined;
        const above = index >= PICTURE_COLUMNS ? page[index - PICTURE_COLUMNS] : undefined;
        const neighbours = [left, above].filter((p): p is ProblemData => !!p).map(factId);
        const n = dealt.get(color)!;
        // Try a few cards ahead for one that is not the same fact as a neighbour.
        let pick = deck[n % deck.length];
        for (let skip = 1; skip < Math.min(deck.length, 4) && neighbours.includes(factId(pick)); skip++) {
            pick = deck[(n + skip) % deck.length];
        }
        dealt.set(color, n + 1);
        page.push({ ...pick, key: index, colorCell: { index, color } });
    });
    return page;
}

/**
 * Color-by-number pages (ADR 0058): the problems come from the arithmetic
 * generator itself, called back with this sheet's settings, so every rule a
 * fact sheet enforces applies here too. Null when the settings give fewer
 * different answers than the picture has colours.
 */
export function generateColorByNumberPages(settings: ISettingsProps, generate: Generate): ProblemData[][] | null {
    const pages: ProblemData[][] = [];
    for (let i = 0; i < settings.NumberOfPages; i++) {
        const pool = generate(poolSettings(settings))?.flat() ?? [];
        const page = pool.length > 0 ? buildColorByNumberPage(pool) : null;
        if (!page) {
            return null;
        }
        pages.push(page);
    }
    return pages;
}
